// src/pages/retail-shops/WalletDailyLimits.jsx
import React, {
  useState, useEffect, useCallback, useRef, useMemo, memo,
} from 'react';
import axios from 'axios';
import {
  Box, Typography, Grid, Card, CardContent, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  LinearProgress, Alert, Snackbar,
  TablePagination, InputAdornment, CircularProgress, Fade, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar, alpha, Stack, Skeleton, Button, Switch, FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon, Clear as ClearIcon, Close as CloseIcon,
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  AccountBalanceWallet as WalletIcon, TrendingUp as TrendingUpIcon,
  Warning as WarningIcon, CheckCircle as CheckCircleIcon,
  History as HistoryIcon, Refresh as RefreshIcon, Lock as LockIcon,
  LockOpen as LockOpenIcon, Speed as SpeedIcon,
  Public as GlobalIcon, Save as SaveIcon,
} from '@mui/icons-material';

import '../../styles/Waiters.css';
import Sidebar from '../../components/finance/Sidebar';
import Header from '../../components/finance/Header';
import Footer from '../../components/finance/Footer';
import { ACCE_BRAND, glossyButton } from '../../theme/colors';

// ════════════════════════════════════════════
// API
// ════════════════════════════════════════════
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
const API_PREFIX   = process.env.REACT_APP_API_PREFIX   || '/api';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const api = axios.create({
  baseURL: FULL_API_URL,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const extractError = (err) => {
  const d = err?.response?.data;
  if (!d) return err.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (d.errors) {
    const first = Object.values(d.errors)[0];
    return Array.isArray(first) ? first[0] : String(first);
  }
  return d.message || 'Request failed';
};

// ── Per-wallet limits ──
const apiList = async (params = {}) => {
  try {
    const { data } = await api.get('/tuckshop/wallets/daily-limits', { params });
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiShow = async (staffId) => {
  try {
    const { data } = await api.get(`/tuckshop/wallets/${staffId}/daily-limit`);
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiSet = async (staffId, payload) => {
  try {
    const { data } = await api.post(`/tuckshop/wallets/${staffId}/daily-limit`, payload);
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiClear = async (staffId) => {
  try {
    const { data } = await api.delete(`/tuckshop/wallets/${staffId}/daily-limit`);
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

// ── Global limit ──
const apiGetGlobal = async () => {
  try {
    const { data } = await api.get('/tuckshop/settings/daily-limit');
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiSetGlobal = async (daily_limit) => {
  try {
    const { data } = await api.put('/tuckshop/settings/daily-limit', { daily_limit });
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiClearGlobal = async () => {
  try {
    const { data } = await api.delete('/tuckshop/settings/daily-limit');
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

// ════════════════════════════════════════════
// UTILITIES
// ════════════════════════════════════════════
const formatCurrency = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `₦${n.toLocaleString('en-NG', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: '2-digit',
  });
};

const safeNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const initials = (name) => {
  if (!name) return '?';
  return String(name).split(' ').filter(Boolean).slice(0, 2)
    .map((s) => s[0]).join('').toUpperCase();
};

// ════════════════════════════════════════════
// KPI CARD
// ════════════════════════════════════════════
const KpiCard = memo(({ label, value, sub, icon, color }) => (
  <Card elevation={0} sx={{
    borderRadius: '14px',
    border: `1px solid ${alpha(color, 0.15)}`,
    bgcolor: '#fff', height: '100%', position: 'relative', overflow: 'hidden',
    transition: 'all 0.25s ease',
    '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 10px 26px ${alpha(color, 0.12)}` },
    '&::before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: color },
  }}>
    <CardContent sx={{ p: 2, pl: 2.4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" sx={{
            color: '#64748B', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.6px', fontSize: '0.65rem',
            fontFamily: '"Inter", sans-serif', display: 'block', mb: 0.5,
          }}>{label}</Typography>
          <Typography variant="h6" fontWeight={800} sx={{
            fontSize: { xs: '1.05rem', sm: '1.25rem' },
            color: '#0F172A', lineHeight: 1.15,
            fontFamily: '"Inter", sans-serif', letterSpacing: '-0.5px',
          }}>{value}</Typography>
          {sub && (
            <Typography variant="caption" sx={{
              color: '#64748B', fontWeight: 600, fontSize: '0.68rem',
              fontFamily: '"Inter", sans-serif', display: 'block', mt: 0.4,
            }}>{sub}</Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: alpha(color, 0.1), color, width: 40, height: 40, borderRadius: '10px' }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
));
KpiCard.displayName = 'KpiCard';

// ════════════════════════════════════════════
// GLOBAL DEFAULT LIMIT CARD
// ════════════════════════════════════════════
const GlobalLimitCard = memo(({
  data, loading, limitValue, setLimitValue, submitting,
  onSave, onDisable, onRefresh,
}) => {
  const isActive = Number(data?.global_limit) > 0;
  const presets = [1000, 2000, 3000, 5000, 10000, 20000, 50000];

  const preview = useMemo(() => {
    const n = Number(limitValue);
    if (!Number.isFinite(n) || n <= 0) return null;
    return { daily: n, weekly: n * 7, monthly: n * 30 };
  }, [limitValue]);

  return (
    <Card elevation={0} sx={{
      borderRadius: '16px', mb: 3, overflow: 'hidden',
      border: `1px solid ${alpha(ACCE_BRAND.orange, 0.2)}`,
      bgcolor: isActive ? alpha('#00897B', 0.02) : '#fff',
    }}>
      <Box sx={{
        height: 5,
        background: isActive
          ? 'linear-gradient(90deg, #00897B, #4DB6AC)'
          : `linear-gradient(90deg, ${ACCE_BRAND.orange}, ${ACCE_BRAND.orangeLight})`,
      }} />
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Grid container spacing={3}>
          {/* Left: current global status */}
          <Grid item xs={12} lg={5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Avatar sx={{
                bgcolor: alpha(isActive ? '#00897B' : '#94A3B8', 0.15),
                color: isActive ? '#00897B' : '#64748B',
                width: 52, height: 52,
              }}>
                <GlobalIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="caption" sx={{
                  color: '#64748B', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.6px',
                  fontSize: '0.7rem', fontFamily: '"Inter", sans-serif',
                }}>
                  Global Default Cap
                </Typography>
                <Typography variant="h4" fontWeight={800} sx={{
                  color: isActive ? '#00897B' : '#0F172A',
                  fontFamily: '"Inter", sans-serif', lineHeight: 1.1,
                }}>
                  {isActive ? formatCurrency(data.global_limit) : 'Unlimited'}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <WalletIcon sx={{ fontSize: 18, color: '#64748B' }} />
                  <Typography variant="body2" fontWeight={700} fontFamily='"Inter", sans-serif' color="text.secondary">
                    Wallets using global
                  </Typography>
                </Stack>
                <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
                  {data?.wallets_total ?? 0}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TrendingUpIcon sx={{ fontSize: 18, color: '#64748B' }} />
                  <Typography variant="body2" fontWeight={700} fontFamily='"Inter", sans-serif' color="text.secondary">
                    Spent today (all wallets)
                  </Typography>
                </Stack>
                <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
                  {formatCurrency(data?.spent_today)}
                </Typography>
              </Box>
            </Stack>

            {!isActive ? (
              <Alert severity="info" icon={<WarningIcon />} sx={{ mt: 2, borderRadius: 2 }}>
                No global cap — wallets are unlimited unless individually overridden below.
              </Alert>
            ) : (
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 2, borderRadius: 2 }}>
                Every wallet without an override is capped at <strong>{formatCurrency(data.global_limit)}</strong> per day.
              </Alert>
            )}
          </Grid>

          {/* Right: the form */}
          <Grid item xs={12} lg={7}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5, fontFamily: '"Inter", sans-serif' }}>
              Set the Default Cap
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: '"Inter", sans-serif' }}>
              Applies to every wallet that doesn't have a personal override.
            </Typography>

            <Typography variant="caption" fontWeight={800} sx={{ mb: 1, display: 'block', fontFamily: '"Inter", sans-serif', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Quick presets
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2.5, gap: 1 }}>
              {presets.map((p) => (
                <Chip
                  key={p}
                  label={formatCurrency(p)}
                  onClick={() => setLimitValue(String(p))}
                  sx={{
                    fontWeight: 700, fontFamily: '"Inter", sans-serif', fontSize: '0.78rem',
                    bgcolor: String(limitValue) === String(p)
                      ? alpha(ACCE_BRAND.orange, 0.15) : alpha('#000', 0.04),
                    color: String(limitValue) === String(p) ? ACCE_BRAND.orange : '#475569',
                    border: String(limitValue) === String(p)
                      ? `1px solid ${alpha(ACCE_BRAND.orange, 0.35)}`
                      : '1px solid transparent',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(ACCE_BRAND.orange, 0.1) },
                  }}
                />
              ))}
            </Stack>

            <TextField
              fullWidth
              type="number"
              label="Default Daily Limit"
              value={limitValue}
              onChange={(e) => setLimitValue(e.target.value)}
              placeholder="e.g. 5000"
              helperText="Enter 0 or use Disable to remove the cap"
              inputProps={{ min: 0, step: 0.01, style: { fontSize: '1.3rem', fontWeight: 800 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontWeight: 800, color: ACCE_BRAND.orange, fontSize: '1.3rem' }}>₦</Typography>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, mb: 2 } }}
            />

            {preview && (
              <Alert severity="info" icon={<TrendingUpIcon />} sx={{ mb: 2, borderRadius: 2 }}>
                Each wallet can spend up to <strong>{formatCurrency(preview.daily)}</strong> per day,
                {' '}<strong>{formatCurrency(preview.weekly)}</strong> per week, or
                {' '}<strong>{formatCurrency(preview.monthly)}</strong> per month.
              </Alert>
            )}

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                size="large"
                disabled={submitting || !limitValue}
                onClick={onSave}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                sx={{
                  ...glossyButton(ACCE_BRAND.orange, ACCE_BRAND.orangeDark, ACCE_BRAND.orangeLight),
                  textTransform: 'none', fontWeight: 800, flex: 1, borderRadius: 2, py: 1.3,
                }}
              >
                {submitting ? 'Saving…' : 'Apply to All Wallets'}
              </Button>

              {isActive && (
                <Button
                  variant="outlined"
                  size="large"
                  disabled={submitting}
                  onClick={onDisable}
                  startIcon={<DeleteIcon />}
                  sx={{
                    textTransform: 'none', fontWeight: 700, borderRadius: 2,
                    color: '#D32F2F', borderColor: alpha('#D32F2F', 0.4),
                    '&:hover': { borderColor: '#D32F2F', bgcolor: alpha('#D32F2F', 0.04) },
                  }}
                >
                  Disable
                </Button>
              )}

              <IconButton
                onClick={onRefresh}
                disabled={submitting || loading}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, width: 56 }}
              >
                <RefreshIcon />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
});
GlobalLimitCard.displayName = 'GlobalLimitCard';

// ════════════════════════════════════════════
// EDIT / CREATE DIALOG (per-wallet override)
// ════════════════════════════════════════════
const LimitDialog = ({ open, onClose, wallet, globalLimit, onSaved, pushSnackbar }) => {
  const [limitValue, setLimitValue] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setLimitValue('');
      setEffectiveFrom(new Date().toISOString().slice(0, 10));
      setEffectiveTo('');
      setNote('');
      setSubmitting(false);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const presets = [1000, 2000, 3000, 5000, 10000, 20000];

  const handleSubmit = async () => {
    if (!wallet) return;

    const raw = String(limitValue).trim();
    if (!/^\d+(\.\d{1,2})?$/.test(raw)) {
      pushSnackbar('Enter a valid amount (e.g. 5000 or 5000.00). Use 0 to clear.', 'error');
      return;
    }

    setSubmitting(true);
    const res = await apiSet(wallet.staff_id, {
      daily_limit: raw,
      effective_from: effectiveFrom || null,
      effective_to: effectiveTo || null,
      note: note?.trim() || null,
    });
    setSubmitting(false);

    if (!res.success) { pushSnackbar(res.error, 'error'); return; }
    pushSnackbar(res.message, 'success');
    onSaved?.();
    onClose();
  };

  const handleClear = async () => {
    if (!wallet) return;
    if (!window.confirm(`Clear the personal override for ${wallet.holder_name}? This wallet will use the global default.`)) return;

    setSubmitting(true);
    const res = await apiClear(wallet.staff_id);
    setSubmitting(false);

    if (!res.success) { pushSnackbar(res.error, 'error'); return; }
    pushSnackbar(res.message, 'success');
    onSaved?.();
    onClose();
  };

  const preview = useMemo(() => {
    const n = Number(limitValue);
    if (!Number.isFinite(n) || n <= 0) return null;
    return { limit: n, daily: n * 7, monthly: n * 30 };
  }, [limitValue]);

  const hasOverride = Number(wallet?.daily_limit) > 0;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Avatar sx={{ bgcolor: alpha(ACCE_BRAND.orange, 0.12), color: ACCE_BRAND.orange }}>
            <SpeedIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
              {hasOverride ? 'Edit Personal Override' : 'Set Personal Override'}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontFamily='"Inter", sans-serif'>
              {wallet?.holder_name} · {wallet?.staff_id}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} disabled={submitting}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          <strong>Default cap:</strong> {Number(globalLimit) > 0 ? formatCurrency(globalLimit) : 'Unlimited'}
          {' '}— this wallet currently uses{' '}
          <strong>{hasOverride ? 'a personal override' : 'the global default'}</strong>.
        </Alert>

        <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1, fontFamily: '"Inter", sans-serif' }}>
          Quick presets
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3, gap: 1 }}>
          {presets.map((p) => (
            <Chip
              key={p}
              label={formatCurrency(p)}
              onClick={() => setLimitValue(String(p))}
              sx={{
                fontWeight: 700, fontFamily: '"Inter", sans-serif',
                bgcolor: String(limitValue) === String(p)
                  ? alpha(ACCE_BRAND.orange, 0.15)
                  : alpha('#000', 0.04),
                color: String(limitValue) === String(p) ? ACCE_BRAND.orange : '#475569',
                border: String(limitValue) === String(p)
                  ? `1px solid ${alpha(ACCE_BRAND.orange, 0.3)}`
                  : '1px solid transparent',
                cursor: 'pointer',
                '&:hover': { bgcolor: alpha(ACCE_BRAND.orange, 0.1) },
              }}
            />
          ))}
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              inputRef={inputRef}
              fullWidth
              required
              type="number"
              label="Personal Daily Limit"
              value={limitValue}
              onChange={(e) => setLimitValue(e.target.value)}
              placeholder="0.00"
              helperText="Enter 0 to remove the override and fall back to the global default"
              inputProps={{ min: 0, step: 0.01, style: { fontSize: '1.2rem', fontWeight: 700 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontWeight: 800, color: ACCE_BRAND.orange }}>₦</Typography>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Effective From"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Effective To (optional)"
              value={effectiveTo}
              onChange={(e) => setEffectiveTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty for open-ended"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Executive exception"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
        </Grid>

        {preview && (
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }} icon={<TrendingUpIcon />}>
            At this cap the wallet can spend at most:
            <br />
            <strong>{formatCurrency(preview.limit)}</strong> per day ·{' '}
            <strong>{formatCurrency(preview.daily)}</strong> per week ·{' '}
            <strong>{formatCurrency(preview.monthly)}</strong> per month
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {hasOverride && (
          <Button
            onClick={handleClear}
            disabled={submitting}
            startIcon={<DeleteIcon />}
            sx={{ color: '#D32F2F', textTransform: 'none', fontWeight: 700, mr: 'auto' }}
          >
            Remove Override
          </Button>
        )}
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none', fontWeight: 700 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !limitValue}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
          sx={{
            ...glossyButton(ACCE_BRAND.orange, ACCE_BRAND.orangeDark, ACCE_BRAND.orangeLight),
            textTransform: 'none', fontWeight: 800, minWidth: 140,
          }}
        >
          {submitting ? 'Saving…' : 'Save Override'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ════════════════════════════════════════════
// HISTORY DIALOG
// ════════════════════════════════════════════
const HistoryDialog = ({ open, onClose, staffId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !staffId) return;
    setLoading(true);
    setData(null);
    (async () => {
      const res = await apiShow(staffId);
      setLoading(false);
      if (res.success) setData(res);
    })();
  }, [open, staffId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Avatar sx={{ bgcolor: alpha('#00897B', 0.12), color: '#00897B' }}>
            <HistoryIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
              Limit History
            </Typography>
            <Typography variant="caption" color="text.secondary" fontFamily='"Inter", sans-serif'>
              {data?.wallet?.holder_name} · {data?.wallet?.staff_id}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress size={40} />
          </Box>
        ) : data ? (
          <>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: alpha('#00897B', 0.03), borderColor: alpha('#00897B', 0.2) }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5, fontFamily: '"Inter", sans-serif' }}>
                Current
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Limit</Typography>
                  <Typography variant="h6" fontWeight={800} color="#00897B" fontFamily='"Inter", sans-serif'>
                    {Number(data.current?.daily_limit) > 0
                      ? formatCurrency(data.current.daily_limit)
                      : 'Using global'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Spent today</Typography>
                  <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
                    {formatCurrency(data.current?.spent_today)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Remaining</Typography>
                  <Typography variant="h6" fontWeight={800} sx={{ color: '#2E7D32' }} fontFamily='"Inter", sans-serif'>
                    {data.current?.remaining_today === null
                      ? 'Unlimited'
                      : formatCurrency(data.current?.remaining_today)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Utilization</Typography>
                  <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
                    {safeNumber(data.current?.utilization_pct).toFixed(1)}%
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5, fontFamily: '"Inter", sans-serif' }}>
              Override History ({data.history?.length || 0})
            </Typography>

            {(data.history || []).length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body2" fontFamily='"Inter", sans-serif'>
                  No personal overrides — this wallet uses the global default.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Limit</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>From</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>To</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Set by</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.history.map((h) => (
                      <TableRow key={h.id} hover>
                        <TableCell sx={{ fontWeight: 800, color: '#00897B', fontFamily: '"Inter", sans-serif' }}>
                          {formatCurrency(h.daily_limit)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{formatDate(h.effective_from)}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>
                          {h.effective_to ? formatDate(h.effective_to) : 'Open-ended'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={h.is_active ? 'Active' : 'Superseded'}
                            sx={{
                              fontWeight: 700, fontFamily: '"Inter", sans-serif',
                              bgcolor: alpha(h.is_active ? '#2E7D32' : '#9E9E9E', 0.12),
                              color: h.is_active ? '#2E7D32' : '#64748B',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: '#64748B' }}>
                          {h.created_by_name || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: '#64748B', maxWidth: 200 }}>
                          {h.note || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════
const WalletDailyLimits = () => {
  const [activeMenu, setActiveMenu] = useState('Wallet');

  // Global default
  const [globalData, setGlobalData] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(true);
  const [globalLimitValue, setGlobalLimitValue] = useState('');
  const [globalSubmitting, setGlobalSubmitting] = useState(false);

  // Per-wallet list
  const [wallets, setWallets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchValue, setSearchValue] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [onlyOverrides, setOnlyOverrides] = useState(false);

  const [editingWallet, setEditingWallet] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [historyStaffId, setHistoryStaffId] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const debounceRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const pushSnackbar = useCallback((message, severity = 'success') => {
    if (!isMountedRef.current) return;
    setSnackbar({ open: true, message, severity });
  }, []);
  const closeSnackbar = useCallback(() => setSnackbar((s) => ({ ...s, open: false })), []);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchDebounced(searchValue.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchValue]);

  // ════════════════════════════════════════════
  // FETCH GLOBAL
  // ════════════════════════════════════════════
  const fetchGlobal = useCallback(async () => {
    if (!isMountedRef.current) return;
    setGlobalLoading(true);
    const res = await apiGetGlobal();
    if (!isMountedRef.current) return;
    if (res.success) {
      setGlobalData(res);
      setGlobalLimitValue(res.global_limit > 0 ? String(res.global_limit) : '');
    } else {
      pushSnackbar(res.error || 'Failed to load global limit', 'error');
    }
    setGlobalLoading(false);
  }, [pushSnackbar]);

  useEffect(() => { fetchGlobal(); }, [fetchGlobal]);

  const handleGlobalSave = async () => {
    const raw = String(globalLimitValue).trim();
    if (!/^\d+(\.\d{1,2})?$/.test(raw)) {
      pushSnackbar('Enter a valid amount (e.g. 5000 or 5000.00).', 'error');
      return;
    }

    setGlobalSubmitting(true);
    const res = await apiSetGlobal(raw);
    setGlobalSubmitting(false);

    if (!res.success) { pushSnackbar(res.error, 'error'); return; }
    pushSnackbar(res.message, 'success');
    fetchGlobal();
    fetchList();
  };

  const handleGlobalDisable = async () => {
    if (!window.confirm('Disable the global default limit? All wallets without overrides will be unlimited.')) return;
    setGlobalSubmitting(true);
    const res = await apiClearGlobal();
    setGlobalSubmitting(false);

    if (!res.success) { pushSnackbar(res.error, 'error'); return; }
    pushSnackbar(res.message, 'success');
    setGlobalLimitValue('');
    fetchGlobal();
    fetchList();
  };

  // ════════════════════════════════════════════
  // FETCH WALLET LIST
  // ════════════════════════════════════════════
  const fetchList = useCallback(async () => {
    if (!isMountedRef.current) return;
    setIsFetching(true);

    const params = { page: page + 1, page_size: rowsPerPage };
    if (searchDebounced) params.search = searchDebounced;
    if (onlyOverrides) params.has_daily_limit = 1;

    const res = await apiList(params);
    if (!isMountedRef.current) return;

    if (res.success) {
      setWallets(res.data || []);
      setTotal(res.total || 0);
    } else {
      pushSnackbar(res.error || 'Failed to load wallets', 'error');
      setWallets([]);
      setTotal(0);
    }
    setIsFetching(false);
    setLoading(false);
  }, [page, rowsPerPage, searchDebounced, onlyOverrides, pushSnackbar]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // ════════════════════════════════════════════
  // KPIs
  // ════════════════════════════════════════════
  const kpi = useMemo(() => {
    const withOverride = wallets.filter((w) => Number(w.daily_limit) > 0);
    const totalSpentToday = wallets.reduce((a, w) => a + safeNumber(w.spent_today), 0);
    const exhausted = wallets.filter((w) => {
      const limit = Number(w.daily_limit) > 0
        ? Number(w.daily_limit)
        : safeNumber(globalData?.global_limit);
      if (limit <= 0) return false;
      return safeNumber(w.spent_today) >= limit;
    }).length;

    return {
      totalWallets: total,
      withOverride: withOverride.length,
      usingGlobal: Math.max(0, total - withOverride.length),
      totalSpentToday,
      exhausted,
    };
  }, [wallets, total, globalData]);

  // ════════════════════════════════════════════
  // HANDLERS
  // ════════════════════════════════════════════
  const openEdit = useCallback((wallet) => {
    setEditingWallet(wallet);
    setEditDialogOpen(true);
  }, []);

  const closeEdit = useCallback(() => {
    setEditDialogOpen(false);
    setEditingWallet(null);
  }, []);

  const afterSave = useCallback(() => {
    fetchList();
    fetchGlobal();
  }, [fetchList, fetchGlobal]);

  // ════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════
  if (loading || globalLoading) {
    return (
      <div className="epos-wrapper">
        <div className="epos-main-layout">
          <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />
          <div className="epos-main">
            <Header />
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ color: ACCE_BRAND.orange }} />
              <Typography variant="h6" sx={{ mt: 2, fontWeight: 700, fontFamily: '"Inter", sans-serif' }}>
                Loading daily limits…
              </Typography>
            </Box>
          </div>
        </div>
      </div>
    );
  }

  const globalLimit = safeNumber(globalData?.global_limit);

  return (
    <>
      <div className="epos-wrapper">
        <div className="epos-main-layout">
          <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />
          <div className="epos-main">
            <Header />
            <div className="epos-content" style={{ background: '#f5f7fa' }}>

              {/* ══ HERO ══ */}
              <Box sx={{
                bgcolor: 'white', borderRadius: '16px',
                p: { xs: 1.5, sm: 2.5, md: 3 },
                mb: 3, border: `1px solid ${alpha(ACCE_BRAND.orange, 0.15)}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="h4" fontWeight={800} sx={{
                      color: ACCE_BRAND.orange,
                      fontSize: { xs: '1.15rem', sm: '1.6rem', md: '1.8rem' },
                      fontFamily: '"Inter", sans-serif', letterSpacing: '-0.5px',
                    }}>
                      🚦 Daily Spending Limits
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{
                      mt: 0.5, fontWeight: 700, fontFamily: '"Inter", sans-serif',
                    }}>
                      Set one cap for everyone, then override individual wallets as exceptions.
                    </Typography>
                  </Box>
                  <Chip
                    icon={<SpeedIcon />}
                    label="Finance Control"
                    variant="outlined"
                    sx={{
                      borderColor: alpha(ACCE_BRAND.orange, 0.3),
                      color: ACCE_BRAND.orange,
                      fontWeight: 700, fontFamily: '"Inter", sans-serif',
                    }}
                  />
                </Box>
              </Box>

              {/* ══ GLOBAL DEFAULT CARD ══ */}
              <GlobalLimitCard
                data={globalData}
                loading={globalLoading}
                limitValue={globalLimitValue}
                setLimitValue={setGlobalLimitValue}
                submitting={globalSubmitting}
                onSave={handleGlobalSave}
                onDisable={handleGlobalDisable}
                onRefresh={fetchGlobal}
              />

              {/* ══ KPI CARDS ══ */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={6} md={3}>
                  <KpiCard
                    label="Wallets"
                    value={kpi.totalWallets}
                    sub={`${kpi.withOverride} with override · ${kpi.usingGlobal} using global`}
                    icon={<WalletIcon />}
                    color={ACCE_BRAND.orange}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <KpiCard
                    label="Effective Default"
                    value={globalLimit > 0 ? formatCurrency(globalLimit) : 'Unlimited'}
                    sub="Applied to all wallets"
                    icon={<GlobalIcon />}
                    color="#00897B"
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <KpiCard
                    label="Spent Today"
                    value={formatCurrency(kpi.totalSpentToday)}
                    sub="All wallets combined"
                    icon={<TrendingUpIcon />}
                    color="#1565C0"
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <KpiCard
                    label="Exhausted Caps"
                    value={kpi.exhausted}
                    sub="Wallets blocked today"
                    icon={<WarningIcon />}
                    color="#D32F2F"
                  />
                </Grid>
              </Grid>

              {/* ══ FILTER BAR ══ */}
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Search by student ID or name…"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  sx={{
                    flex: 1, minWidth: 240, maxWidth: 420,
                    '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchValue && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchValue('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={onlyOverrides}
                      onChange={(e) => { setOnlyOverrides(e.target.checked); setPage(0); }}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={700} fontFamily='"Inter", sans-serif'>
                      Only wallets with an override
                    </Typography>
                  }
                />

                <Tooltip title="Refresh">
                  <IconButton
                    onClick={() => { fetchList(); fetchGlobal(); }}
                    disabled={isFetching}
                    sx={{
                      bgcolor: alpha(ACCE_BRAND.orange, 0.1),
                      '&:hover': { bgcolor: alpha(ACCE_BRAND.orange, 0.2) },
                      borderRadius: 2,
                    }}
                  >
                    {isFetching ? <CircularProgress size={20} /> : <RefreshIcon sx={{ color: ACCE_BRAND.orange }} />}
                  </IconButton>
                </Tooltip>

                <Typography variant="caption" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>
                  {total} {total === 1 ? 'wallet' : 'wallets'}
                </Typography>
              </Paper>

              {isFetching && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

              {/* ══ TABLE ══ */}
              <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${alpha('#000', 0.06)}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif' }}>
                    Individual Wallets
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontFamily='"Inter", sans-serif'>
                    Wallets without an override inherit the global default.
                  </Typography>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(ACCE_BRAND.orange, 0.04) }}>
                        <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Student</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Student ID</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Balance</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Effective Cap</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Spent Today</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif', minWidth: 160 }}>Utilization</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isFetching && wallets.length === 0 ? (
                        [...Array(5)].map((_, i) => (
                          <TableRow key={i}>
                            {[...Array(7)].map((__, j) => (
                              <TableCell key={j}><Skeleton variant="text" /></TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : wallets.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                            <SpeedIcon sx={{ fontSize: 56, color: 'text.disabled', opacity: 0.4, mb: 1 }} />
                            <Typography variant="h6" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>
                              No wallets found
                            </Typography>
                            <Typography variant="body2" color="text.secondary" fontFamily='"Inter", sans-serif'>
                              Try adjusting your search or toggle the filter.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        wallets.map((w) => {
                          const hasOverride = Number(w.daily_limit) > 0;
                          const effective = hasOverride ? Number(w.daily_limit) : globalLimit;
                          const spentToday = safeNumber(w.spent_today);
                          const remaining = effective > 0 ? Math.max(0, effective - spentToday) : null;
                          const utilization = effective > 0 ? Math.min(100, (spentToday / effective) * 100) : 0;
                          const exhausted = effective > 0 && remaining === 0;

                          const barColor = effective === 0
                            ? '#94A3B8'
                            : exhausted
                              ? '#D32F2F'
                              : utilization >= 75
                                ? '#EF6C00'
                                : '#2E7D32';

                          return (
                            <TableRow key={w.id} hover>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                  <Avatar sx={{
                                    bgcolor: alpha(hasOverride ? '#00897B' : ACCE_BRAND.orange, 0.15),
                                    color: hasOverride ? '#00897B' : ACCE_BRAND.orange,
                                    width: 36, height: 36, fontWeight: 800, fontSize: '0.82rem',
                                  }}>
                                    {initials(w.holder_name)}
                                  </Avatar>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography fontWeight={700} noWrap sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.85rem' }}>
                                      {w.holder_name}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                      {w.is_locked ? (
                                        <Chip size="small" icon={<LockIcon sx={{ fontSize: 11 }} />} label="Locked"
                                          sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha('#D32F2F', 0.12), color: '#D32F2F' }} />
                                      ) : (
                                        <Chip size="small" icon={<LockOpenIcon sx={{ fontSize: 11 }} />} label="Active"
                                          sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha('#2E7D32', 0.12), color: '#2E7D32' }} />
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip size="small" label={w.staff_id}
                                  sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.7rem' }} />
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={800} sx={{ color: ACCE_BRAND.orange, fontFamily: '"Inter", sans-serif' }}>
                                  {formatCurrency(w.balance)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {effective > 0 ? (
                                  <Box sx={{ textAlign: 'right' }}>
                                    <Typography fontWeight={800} sx={{ color: '#00897B', fontFamily: '"Inter", sans-serif' }}>
                                      {formatCurrency(effective)}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={hasOverride ? 'Override' : 'Global'}
                                      sx={{
                                        height: 18, fontSize: '0.6rem', fontWeight: 800, mt: 0.4,
                                        bgcolor: alpha(hasOverride ? '#00897B' : '#64748B', 0.12),
                                        color: hasOverride ? '#00897B' : '#64748B',
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <Chip size="small" label="Unlimited"
                                    sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: alpha('#94A3B8', 0.12), color: '#64748B' }} />
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={700} sx={{ fontFamily: '"Inter", sans-serif' }}>
                                  {formatCurrency(spentToday)}
                                </Typography>
                                {effective > 0 && (
                                  <Typography variant="caption" color="text.secondary" fontFamily='"Inter", sans-serif' display="block">
                                    {formatCurrency(remaining)} left
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {effective > 0 ? (
                                  <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                      <Typography variant="caption" fontWeight={700} sx={{ color: barColor, fontFamily: '"Inter", sans-serif' }}>
                                        {utilization.toFixed(0)}%
                                      </Typography>
                                      {exhausted && (
                                        <Typography variant="caption" fontWeight={800} sx={{ color: '#D32F2F', fontFamily: '"Inter", sans-serif' }}>
                                          Exhausted
                                        </Typography>
                                      )}
                                    </Box>
                                    <LinearProgress
                                      variant="determinate"
                                      value={utilization}
                                      sx={{
                                        height: 6, borderRadius: 3, bgcolor: alpha('#000', 0.06),
                                        '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 3 },
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <Typography variant="caption" color="text.secondary" fontFamily='"Inter", sans-serif'>
                                    No cap
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title={hasOverride ? 'Edit override' : 'Set personal override'}>
                                  <IconButton size="small" onClick={() => openEdit(w)}>
                                    {hasOverride
                                      ? <EditIcon fontSize="small" sx={{ color: '#00897B' }} />
                                      : <AddIcon fontSize="small" sx={{ color: ACCE_BRAND.orange }} />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="View override history">
                                  <IconButton size="small" onClick={() => setHistoryStaffId(w.staff_id)}>
                                    <HistoryIcon fontSize="small" sx={{ color: '#64748B' }} />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                  sx={{
                    borderTop: `1px solid ${alpha('#000', 0.06)}`,
                    '& .MuiTablePagination-select, & .MuiTablePagination-displayedRows': {
                      fontWeight: 700, fontFamily: '"Inter", sans-serif',
                    },
                  }}
                />
              </Paper>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* ══ DIALOGS ══ */}
      <LimitDialog
        open={editDialogOpen}
        onClose={closeEdit}
        wallet={editingWallet}
        globalLimit={globalLimit}
        onSaved={afterSave}
        pushSnackbar={pushSnackbar}
      />

      <HistoryDialog
        open={Boolean(historyStaffId)}
        onClose={() => setHistoryStaffId(null)}
        staffId={historyStaffId}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled"
          sx={{ borderRadius: '10px', fontWeight: 700, fontFamily: '"Inter", sans-serif' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WalletDailyLimits;